import type { ParseKeys } from "i18next";

import type { IFileSize, IFileType } from "./types";

import {
  AUDIO_FORMATS,
  DOCUMENT_FORMATS,
  EXCEL_FORMATS,
  fileSizeUnit,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  ZIP_FORMATS,
} from "@/constants";

export function getFileTypeFormat(type: IFileType): string[] {
  switch (type) {
    case "audio":
      return AUDIO_FORMATS;
    case "video":
      return VIDEO_FORMATS;
    case "excel":
      return EXCEL_FORMATS;
    case "image":
      return IMAGE_FORMATS;
    case "document":
      return DOCUMENT_FORMATS;
    case "zip":
      return ZIP_FORMATS;
    case "multiType":
      return ["*/*"];
  }
}

export function getFileTypeName(type: IFileType): ParseKeys<"form"> {
  switch (type) {
    case "image":
      return "fileInput.image";
    case "excel":
      return "fileInput.csv";
    default:
      return "fileInput.file";
  }
}

/**
 * Iterates through a given FileList and returns an array of File objects. If oldFiles are provided, they are concatenated to the end of the array.
 *
 * @param {FileList} fileList - The list of files to iterate through.
 * @param {File[] | undefined} oldFiles - Optional array of File objects to concatenate to the end of the resulting array.
 * @returns {File[]} - An array of File objects.
 */
export function fileListIterator(
  fileList: FileList | null,
  oldFiles: File[] | undefined,
): File[] {
  const temp: File[] = [];

  if (fileList !== null) {
    for (const element of fileList) {
      temp.push(element);
    }
  }
  if (oldFiles) {
    return [...temp, ...oldFiles];
  }
  return temp;
}

/**
 * Validates a given FileList based on the specified file type and maximum size, and sets an error message if necessary.
 *
 * @param {FileList | null} fileList - The list of files to validate.
 * @param {IFileType} type - The expected file type.
 * @param {IFileSize } maxSize - The maximum size in bytes allowed for all files combined.
 * @param {number } maxFilesLength - The maximum length of files.
 * @param {(error: string) => void} setError - A callback function to set the error message.
 * @returns {boolean} - True if the FileList is valid, false otherwise.
 */
export function validateFileList(
  fileList: FileList | File[] | null,
  type: IFileType,
  maxSize: IFileSize,
  maxFilesLength: number,
  setError: (error: ParseKeys<"form">) => void,
): boolean {
  if (fileList === null) return true;

  if (fileList.length > maxFilesLength) {
    // * this is the key of translation object
    setError("errors.invalidFileMaxCount");
    return false;
  }

  const totalSize = getTotalSize(fileList);

  if (totalSize > getActualFileSize(maxSize)) {
    // * this is the key of translation object
    setError("errors.invalidFileSize");
    return false;
  }

  if (type === "excel") {
    for (const element of fileList) {
      if (!EXCEL_FORMATS.includes(element.type)) {
        // * this is the key of translation object
        setError("errors.invalidFileFormat");
        return false;
      }
    }
  } else if (type === "image") {
    for (const element of fileList) {
      if (!IMAGE_FORMATS.includes(element.type)) {
        // * this is the key of translation object
        setError("errors.invalidFileFormat");
        return false;
      }
    }
  }
  return true;
}

/**
 * Calculates the total size of all files in a given FileList.
 *
 * @param {FileList} fileList - The list of files to calculate the total size of.
 * @returns {number} - The total size of all files in bytes.
 */
function getTotalSize(fileList: FileList | File[]): number {
  let size = 0;

  for (const element of fileList) {
    size += element.size;
  }
  return size;
}

/**
 * get the file size in bytes in convert to KB, MB, GB
 * @param size file size
 */
export function humanizedFileSize(size: number) {
  if (size === 0) return "0 Bytes";
  const kilo = 1000;
  const dm = 2;
  const i = Math.floor(Math.log(size) / Math.log(kilo));

  return `${Number.parseFloat((size / kilo ** i).toFixed(dm))} ${
    fileSizeUnit[i]
  }`;
}

/**
 * Converts a human-readable file size string to its equivalent size in bytes.
 *
 * @param {IFileSize} sizeObject - The human-readable file size string to convert.
 * @returns {number} - The equivalent size in bytes.
 */
export function getActualFileSize(sizeObject: IFileSize): number {
  const size = sizeObject.size;
  const unit = sizeObject.unit;
  const index = fileSizeUnit.indexOf(unit);

  return size * 1000 ** index;
}
